<template>
  <div
    v-if="shouldShow"
    class="trial-studio"
  >
    <div v-if="shouldShowModal">
      <component
        :is="linkElement"
        class="trial-studio__link"
        :href="link"
        target="_blank"
      >
        <span class="trial-studio__title">Studio</span>
        <span
          class="trial-studio__label"
          :class="labelClassVariation"
        >{{ label }}</span>
      </component>

      <button
        type="button"
        class="trial-studio__button"
        @click="openModal = !openModal"
      >
        Adquirir
      </button>
    </div>

    <DsModal
      :open="openModal"
      :title="title"
      :text="text"
      :primary-button-label="primaryButton"
      :secondary-button-label="secondaryButton"
      @primary="primaryButtonAction"
      @secondary="secondaryButtonAction"
    >
      <template slot="content">
        <div class="ds-radio-button--row">
          <div
            v-for="option in labels"
            :key="option.id"
          >
            <div class="ds-radio-button">
              <input
                :id="option.id"
                v-model="radioSelected"
                type="radio"
                :name="option.title"
                :value="option.id"
              >
              <label :for="option.id">{{ option.title }} </label>
            </div>
          </div>
        </div>
      </template>
    </DsModal>

    <DsModal
      :open="openModalConfirm"
      :title="titleConfirm"
      :text="textConfirm"
      :primary-button-label="primaryButtonConfirm"
      @primary="finishFlow"
    />
  </div>
</template>

<script>
import DashboardService from './services/DashboardService';
import TrialMailService from './services/TrialMailService';
import { DsModal } from '@ds/components';

const convertMiliToDays = (mili) => Math.ceil(mili / 1000 / 60 / 60 / 24);
const LIMIT_EXPIRED_DAYS_TO_SHOW_INTERVENTION = -14;

export default {
  name: 'TrialStudio',
  components: {
    DsModal,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    universeId: {
      type: String,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    isStudio: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      daysRemaining: null,
      openModal: false,
      openModalConfirm: false,
      title: 'Bora ter o seu STILINGUE Studio?',
      testIsOverText: 'O período de teste acabou, mas você pode entrar em contato com o Customer Success responsável pela sua conta para garantir que você tenha o melhor plano. Mas antes, conta pra gente quantos dashboards você acredita que sejam necessários para a sua operação:',
      bestPlanText: 'Agora é só entrar em contato com o Customer Success responsável pela sua conta para garantir que você tenha o melhor plano. Mas antes, conta pra gente quantos dashboards você acredita que sejam necessários para a sua operação:',
      primaryButton: 'Confirmar',
      cancel: false,
      confirm: false,
      labels: [
        { title: 'Apenas 1', id: 0 },
        { title: 'Até 6', id: 1 },
        { title: 'Mais de 6', id: 2 },
      ],
      radioSelected: 0,
      titleConfirm: 'Análise de plano confirmada',
      textConfirm: 'Agora é só aguardar o contato do seu Customer Success para escolher o melhor plano para a sua operação.',
      primaryButtonConfirm: 'Entendi',
    };
  },
  computed: {
    linkElement() {
      if (this.isStudioOrTrialExpired) {
        return 'span';
      }

      return 'a';
    },
    link() {
      if (this.isStudioOrTrialExpired) {
        return;
      }

      return `https://studio.stilingue.com.br/${this.accountId}/${this.universeId}/dashboard`;
    },
    isExpired() {
      return this.daysRemaining <= 0;
    },
    labelClassVariation() {
      const classPrefix = 'trial-studio__label';

      if (this.isExpired) {
        return `${classPrefix}--expired`;
      }

      if (this.daysRemaining <= 7) {
        return `${classPrefix}--warning`;
      }

      return `${classPrefix}--default`;
    },
    label() {
      if (this.isExpired) {
        return 'Teste encerrado';
      }

      const daysInPlural = this.daysRemaining > 1 ? 's' : '';
      return `${this.daysRemaining} dia${daysInPlural} restante${daysInPlural}`;
    },
    text() {
      if (this.isExpired) {
        return this.testIsOverText;
      }

      return this.bestPlanText;
    },
    secondaryButton() {
      if (this.isStudioAndTrialExpired) {
        return '';
      }

      return 'Cancelar';
    },
    shouldShow() {
      if (this.daysRemaining === null) {
        return false;
      }

      if (!this.isStudio && this.daysRemaining < LIMIT_EXPIRED_DAYS_TO_SHOW_INTERVENTION) {
        return false;
      }

      return true;
    },
    shouldShowModal() {
      if (this.isStudioAndTrialExpired) {
        return false;
      }

      return true;
    },
    isStudioAndTrialExpired() {
      return this.isStudio && this.isExpired;
    },
    isStudioOrTrialExpired() {
      return this.isStudio || this.isExpired;
    }
  },
  async created() {
    const dashboardService = new DashboardService({
      universeId: this.universeId,
      userEmail: this.user.email,
    });

    const dashboard = await dashboardService.getDashboard();
    const expiresTrialDate = dashboard.data().expiresTrialDate;

    if (!expiresTrialDate) {
      return;
    }

    const expiresDate = expiresTrialDate.toDate();

    if (!dashboard || !expiresDate) {
      return;
    }

    this.daysRemaining = convertMiliToDays(expiresDate - new Date());

    if (this.isStudioAndTrialExpired) {
      this.openModal = true;
    }
  },
  methods: {
    primaryButtonAction() {
      this.openModal = false;

      if (this.radioSelected !== null) {
        const user = this.user;
        const accountId = this.accountId;
        const universeId = this.universeId;
        const dashboardsQuantity = this.labels[this.radioSelected].title;

        const trialMailService = new TrialMailService({
          user,
          accountId,
          universeId,
          dashboardsQuantity,
        });
        trialMailService.send();

        this.openModalConfirm = true;
      }

      this.radioSelected = 0;
    },
    secondaryButtonAction() {
      this.openModal = false;
    },
    finishFlow() {
      if (this.isStudioAndTrialExpired) {
        window.open(process.env.VUE_APP_WARROOM_URL, '_blank');
        return;
      }

      this.openModalConfirm = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@ds/tokens';
@import '~@ds/styles/RadioGroup.scss';
@import '~@ds/styles/RadioButton.scss';

.trial-studio {
  margin-right: 48px;

  &__link {
    display: inline-flex;
    align-items: center;
    color: var(--ds-color-white-text);
  }

  &__title {
    margin-right: 2px;
    font-size: var(--ds-font-size-baseline);
    line-height: var(--ds-font-line-height-normal);
    font-weight: var(--ds-font-weight-semibold);
  }

  &__label {
    margin-right: var(--ds-spacing-stack-nano);
    padding: 2px 4px;
    font-size: 10px;
    line-height: var(--ds-font-line-height-slim);
    font-weight: var(--ds-font-weight-bold);
    border-radius: var(--ds-border-radius-md);
    color: var(--ds-color-text-button);

    &--default {
      background-color: var(--ds-color-mantis);
    }

    &--warning {
      background-color: var(--ds-color-butter);
    }

    &--expired {
      background-color: var(--ds-color-scarlet);
    }
  }

  &__button {
    font-size: var(--ds-font-size-xs);
    line-height: var(--ds-font-line-height-dense);
    font-weight: var(--ds-font-weight-semibold);
    text-decoration-line: underline;
    background: none;
    border: 0;
    color: var(--ds-color-white-text);
  }
}
</style>
